// Workflow section — live business pulse from the workflow app's management
// dashboard (GET /management/dashboard → getManagementDashboard). Six pulse
// tiles (pipeline value, revenue MTD, open AR, AP outstanding, active orders,
// new clients MTD) plus the two backlog tiles (unreviewed sign-ups, pending
// project requests). Each tile deep-links into the workflow app at
// app.getmerchd.com. Data-fetch + loading/error patterns mirror FinancePage.
import { useCallback, useEffect, useState } from "react";
import { LayoutGrid, ExternalLink, RefreshCw, ArrowUpRight } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

// The workflow app on the same family of domains as this hub (other build
// on *.withother.com, the team's on *.getmerchd.com).
const WORKFLOW_APP = typeof window !== "undefined" && window.location.hostname.endsWith("withother.com")
  ? "https://flow.withother.com"
  : "https://app.getmerchd.com";

// ─── Types (mirror apps/api/src/services/management-dashboard.ts) ─────────────

interface ManagementKpi {
  slug: string;
  label: string;
  value: number;
  format: "count" | "currency";
  href: string;
  description: string;
}

interface ManagementDashboard {
  pulse: ManagementKpi[];
  backlog: ManagementKpi[];
  fetchedAt: string;
}

// ─── Formatting ───────────────────────────────────────────────────────────────

function fmtMoney(n: number): string {
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  return n < 0 ? `−${formatted}` : formatted;
}

function fmtValue(kpi: ManagementKpi): string {
  return kpi.format === "currency"
    ? fmtMoney(kpi.value)
    : kpi.value.toLocaleString("en-US");
}

// ─── KPI tile (deep-links into the workflow app) ──────────────────────────────

function KpiCard({ kpi, tone = "neutral" }: { kpi: ManagementKpi; tone?: "neutral" | "warn" }) {
  return (
    <a
      href={`${WORKFLOW_APP}${kpi.href}`}
      target="_blank"
      rel="noopener noreferrer"
      title={kpi.description}
      className="group flex flex-col rounded-lg border border-brown-100 bg-white p-5 transition-colors hover:border-brown-300"
    >
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-brown-300">
          {kpi.label}
        </p>
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted transition-colors group-hover:text-peach-500" />
      </div>
      <p
        className={cn(
          "mt-1 font-display text-2xl font-semibold",
          tone === "warn" && kpi.value > 0 ? "text-amber-600" : "text-brown-500",
        )}
      >
        {fmtValue(kpi)}
      </p>
      <p className="mt-1 text-xs leading-snug text-brown-400">{kpi.description}</p>
    </a>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function WorkflowPage() {
  const [dashboard, setDashboard] = useState<ManagementDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (refresh: boolean) => {
    if (refresh) setRefreshing(true);
    setError(null);
    try {
      const data = await api<ManagementDashboard>("/management/dashboard");
      setDashboard(data);
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to load workflow metrics"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
  }, [load]);

  return (
    <div className="mx-auto max-w-5xl">
      <SectionHeader
        icon={LayoutGrid}
        title="Flow"
        description="Pipeline health, revenue, and open work across all reps."
      />

      <div className="mt-8">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-peach-400 border-t-transparent" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-rust-300 bg-rust-50 px-5 py-4">
            <p className="text-sm font-semibold text-rust-700">
              Couldn't load workflow metrics: {error}
            </p>
            <button
              onClick={() => void load(true)}
              className="mt-2 text-sm font-semibold text-ink underline"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && dashboard && (
          <div className="space-y-6">
            {/* Toolbar */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-brown-300">
                {dashboard.fetchedAt
                  ? `As of ${new Date(dashboard.fetchedAt).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}`
                  : ""}
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => void load(true)}
                  disabled={refreshing}
                  className="flex items-center gap-1.5 rounded-md border border-brown-200 bg-white px-3 py-1.5 text-sm font-semibold text-brown-400 hover:border-brown-300 hover:text-brown-500 transition-colors disabled:opacity-50"
                >
                  <RefreshCw
                    className={cn("h-3.5 w-3.5", refreshing && "animate-spin")}
                  />
                  Refresh
                </button>
                <a
                  href={WORKFLOW_APP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 rounded-md border border-brown-200 bg-white px-3 py-1.5 text-sm font-semibold text-brown-400 hover:border-brown-300 hover:text-brown-500 transition-colors"
                >
                  Open Flow
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            {/* Pulse tiles */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {dashboard.pulse.map((kpi) => (
                <KpiCard key={kpi.slug} kpi={kpi} />
              ))}
            </div>

            {/* Backlog — work waiting on a decision */}
            {dashboard.backlog.length > 0 && (
              <div>
                <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-brown-300">
                  Waiting on you
                </p>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  {dashboard.backlog.map((kpi) => (
                    <KpiCard key={kpi.slug} kpi={kpi} tone="warn" />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface SectionHeaderProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

export function SectionHeader({ icon: Icon, title, description }: SectionHeaderProps) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brown-900">
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <h1 className="font-display text-2xl font-semibold text-brown-500">
          {title}
        </h1>
        <p className="mt-0.5 text-sm text-brown-400">{description}</p>
      </div>
    </div>
  );
}
